import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { parseVCard } from "../lib/vcard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { UserPlus, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import CompanySearchSelect from "../components/companies/CompanySearchSelect";
import { useAuth } from "../context/AuthContext";

const SHARE_CACHE = "namedrop-share-v1";
const SHARE_KEY = "/__namedrop_share_payload__";

const EMPTY_FORM = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    company_id: "",
    company_name: "",
    city: "",
    notes: "",
};

/**
 * Merge a vCard-derived object with the raw text fields submitted by the
 * sharing app. Explicit text values win when present (they're what the user
 * typed in the share sheet).
 */
function mergeShare(payload) {
    const v = parseVCard(payload?.vcard || "");
    const sharedName = (payload?.name || "").trim();
    let first = v.first_name || "";
    let last = v.last_name || "";
    if (sharedName) {
        const parts = sharedName.split(/\s+/);
        first = parts.shift() || first;
        const restLast = parts.join(" ");
        if (restLast) last = restLast;
    }
    if (payload?.lastname) last = payload.lastname.trim();

    return {
        first_name: first || "",
        last_name: last || "",
        email: (payload?.email || v.email || "").trim(),
        phone: (payload?.phone || v.phone || "").trim(),
        position: (payload?.cargo || v.position || "").trim(),
        company_id: "",
        company_name: (payload?.company || v.company_name || "").trim(),
        city: (v.city || "").trim(),
        notes: payload?.url ? `URL compartida: ${payload.url}` : "",
    };
}

export default function ShareTarget() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canCreateCompany = hasPermission("companies_manage");
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasShared, setHasShared] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!("caches" in window)) {
                    setLoading(false);
                    return;
                }
                const cache = await caches.open(SHARE_CACHE);
                const resp = await cache.match(SHARE_KEY);
                if (resp) {
                    const payload = await resp.json();
                    // Consume once so reloads don't reuse stale data.
                    await cache.delete(SHARE_KEY);
                    if (!cancelled) {
                        setForm(mergeShare(payload));
                        setHasShared(true);
                    }
                }
            } catch (_) {
                // Ignore: page still renders an empty form the user can fill.
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.first_name.trim()) {
            toast.error("El nombre es obligatorio");
            return;
        }
        if (!form.company_id) {
            toast.error("Selecciona una empresa existente");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim() || "",
                email: form.email.trim() || null,
                phone: form.phone.trim() || null,
                position: form.position.trim() || null,
                company_id: form.company_id,
                city: form.city.trim() || null,
                notes: form.notes.trim() || null,
                source: "namedrop_share",
            };
            const { data } = await api.post("/leads/share", payload);
            toast.success(`Contacto guardado: ${data.first_name || ""} ${data.last_name || ""}`.trim());
            navigate("/leads", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409) {
                toast.error(
                    typeof detail === "object" && detail?.message
                        ? detail.message
                        : "Ya existe un contacto con este correo"
                );
            } else {
                toast.error(formatApiError(detail) || "No se pudo guardar el contacto");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout
            title="Nuevo contacto compartido"
            subtitle="Revisa los datos recibidos y guarda el contacto"
            data-testid="share-target-page"
        >
            <div className="max-w-2xl mx-auto">
                <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
                    <div className="flex items-start gap-3 mb-5">
                        <div className="w-10 h-10 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                            <Share2 className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-display font-semibold text-slate-900">
                                Contacto recibido
                            </h2>
                            <p className="text-sm text-slate-500">
                                {hasShared
                                    ? "Hemos detectado un contacto compartido desde tu dispositivo. Revisa la información antes de guardarla."
                                    : "Completa los datos del contacto y se guardará en tu CRM."}
                            </p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="py-12 flex items-center justify-center text-slate-400 text-sm" data-testid="share-target-loading">
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />Cargando datos compartidos...
                        </div>
                    ) : (
                        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="share-target-form">
                            <div>
                                <Label>Nombre *</Label>
                                <Input
                                    value={form.first_name}
                                    onChange={(e) => update("first_name", e.target.value)}
                                    required
                                    data-testid="share-form-first-name"
                                />
                            </div>
                            <div>
                                <Label>Apellido</Label>
                                <Input
                                    value={form.last_name}
                                    onChange={(e) => update("last_name", e.target.value)}
                                    data-testid="share-form-last-name"
                                />
                            </div>
                            <div>
                                <Label>Correo</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    data-testid="share-form-email"
                                />
                            </div>
                            <div>
                                <Label>Teléfono</Label>
                                <Input
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    data-testid="share-form-phone"
                                />
                            </div>
                            <div>
                                <Label>Cargo</Label>
                                <Input
                                    value={form.position}
                                    onChange={(e) => update("position", e.target.value)}
                                    data-testid="share-form-position"
                                />
                            </div>
                            <div>
                                <Label>Empresa *</Label>
                                <CompanySearchSelect
                                    value={form.company_id}
                                    onChange={(company_id, company) => setForm((current) => ({ ...current, company_id, company_name: company?.name || current.company_name }))}
                                    canCreate={canCreateCompany}
                                    data-testid="share-form-company"
                                />
                                {form.company_name && !form.company_id && <p className="mt-1 text-xs text-slate-500">Detectado en el contacto: {form.company_name}. Busca y selecciona la empresa existente.</p>}
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Ciudad</Label>
                                <Input
                                    value={form.city}
                                    onChange={(e) => update("city", e.target.value)}
                                    data-testid="share-form-city"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Notas</Label>
                                <Textarea
                                    rows={3}
                                    value={form.notes}
                                    onChange={(e) => update("notes", e.target.value)}
                                    data-testid="share-form-notes"
                                />
                            </div>
                            <div className="sm:col-span-2 flex items-center justify-end gap-2 mt-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => navigate("/leads")}
                                    disabled={saving}
                                    data-testid="share-form-cancel"
                                >
                                    Cancelar
                                </Button>
                                <Button type="submit" disabled={saving} data-testid="share-form-save">
                                    {saving ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                                    ) : (
                                        <><UserPlus className="w-4 h-4 mr-2" />Guardar contacto</>
                                    )}
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </Layout>
    );
}
